import { useEffect, useState } from 'react';
import Video, { LocalAudioTrack, LocalVideoTrack, LocalDataTrack, Room } from 'twilio-video';
import { useCall } from '../context';
import * as Sentry from '@sentry/react';

export interface UseRoomOptions {
    token: string | null;
    region: string | null;
    videoTracks: LocalVideoTrack[];
    audioTracks: LocalAudioTrack[];
    dataTrack: LocalDataTrack;
}

export interface UseRoomResult {
    room: Room | null;
    connecting: boolean;
    error: string | null;
}

export default function useRoom({
    token,
    region,
    videoTracks,
    audioTracks,
    dataTrack,
}: UseRoomOptions): UseRoomResult {
    const [room, setRoom] = useState<Room | null>(null);
    const [connecting, setConnecting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { storeTwilioRoom, hangup } = useCall();

    const connect = async () => {
        if (token && region) {
            setConnecting(true);
            try {
                const twilioRoom = await Video.connect(token, {
                    region: region as any,
                    name: 'room',
                    tracks: [...videoTracks, ...audioTracks, dataTrack],
                });

                setRoom(twilioRoom);
                storeTwilioRoom(twilioRoom);
            } catch (err) {
                setError('The call has already ended.');
                hangup();
                Sentry.captureException(err, { tags: { feature: 'twilio-connect' } });
            }
            setConnecting(false);
        }
    };

    const disconnect = () => {
        if (room) {
            room.disconnect();
        }
    };

    useEffect(() => {
        connect();
        return () => {
            disconnect();
        };
    }, []);

    return { room, connecting, error };
}
